import { BRAND } from './brand';

import { CARDS, cardSlug, type Card } from './cards';
import { MAJOR_MEANINGS } from './major-meanings';
import { GLOSSARY } from './glossary';
import { FREE_LIMIT } from './quota';

/**
 * 페이지별 검색엔진 메타데이터 — **단일 출처**.
 *
 * 세 곳이 같은 값을 써야 어긋나지 않는다:
 *  ① 빌드 시 라우트별 정적 HTML 생성 (apps/web/vite.config.ts)
 *  ② SPA 안에서 이동할 때의 document.title 갱신 (useDocumentMeta)
 *  ③ sitemap.xml 목록
 *
 * 왜 정적 HTML까지 만드는가:
 *  SPA는 어느 주소로 들어와도 같은 index.html을 받는다. 그러면 카드 78장이
 *  전부 같은 title에 canonical이 홈으로 박힌 문서로 보여서, 구글은 중복으로 판단해
 *  홈으로 병합하고 네이버 봇(자바스크립트 실행이 제한적)은 내용을 아예 못 읽는다.
 */

/** 검색결과에 잘리지 않는 설명 길이 (구글 데스크톱 기준 대략치) */
const DESCRIPTION_MAX = 155;

const clip = (s: string, max = DESCRIPTION_MAX) => {
  const flat = s.replace(/\s+/g, ' ').trim();
  return flat.length <= max ? flat : `${flat.slice(0, max - 1).trimEnd()}…`;
};

export interface PageLink {
  path: string;
  label: string;
}

export interface PageSeo {
  /** 사이트 루트 기준 경로 (예: /cards/the-fool) */
  path: string;
  title: string;
  description: string;
  /** sitemap 우선순위 */
  priority: string;
  /** 크롤러가 자바스크립트 없이도 읽을 본문 (정적 HTML에 심는다) */
  body?: string[];
  /**
   * 사이트 공통 메뉴에 넣을 짧은 이름. 있으면 **모든** 정적 HTML의 링크 목록에 들어간다.
   * 화면 푸터 메뉴와 같은 구성이어야 한다 — 정적 HTML에만 있는 링크는 클로킹으로 읽힌다.
   */
  navLabel?: string;
  /** 이 페이지에서만 나가는 링크 (도감 → 카드 78장, 카드 → 이웃 카드) */
  links?: readonly PageLink[];
}

/** 카드 링크에 쓰는 표기 — 도감 목록과 이웃 링크가 어긋나지 않게 한 곳에서 만든다 */
const cardLinkLabel = (c: Card) => `${c.nameKo}(${c.nameEn}) 카드 의미`;

/** 제목 뒤에 붙는 브랜드 꼬리표 — 검색결과에서 출처를 알아보게 한다 */
const withBrand = (title: string) => `${title} | ${BRAND.nameKo}`;

/** 카드 1장의 메타데이터. 메이저 22장은 해설이 있어 설명·본문이 훨씬 풍부하다 */
export function cardSeo(card: Card, index?: number): PageSeo {
  const meaning = MAJOR_MEANINGS.find((m) => m.id === card.id && card.arcana === 'major');
  const keywords = card.keywordsUp.join(' · ');
  /**
   * 이웃 카드로 이어 두면 78장이 하나의 사슬이 된다. 사이트맵에만 있고 링크가 없는 주소는
   * 구글이 "발견됨 — 색인 생성되지 않음"으로 미뤄 둔다 (2026-09-07 서치콘솔 실측 71건).
   */
  const i = index ?? CARDS.findIndex((c) => cardSlug(c) === cardSlug(card));
  const neighbors = [CARDS[i - 1], CARDS[i + 1]].filter((c): c is Card => Boolean(c));
  return {
    path: `/cards/${cardSlug(card)}`,
    title: withBrand(`${card.nameKo}(${card.nameEn}) 타로 카드 의미`),
    description: meaning
      ? clip(meaning.upright)
      : clip(
          `${card.nameKo}(${card.nameEn}) 카드의 정방향 의미는 ${keywords}. 역방향은 ${card.keywordsRev.join(' · ')}.`,
        ),
    priority: '0.7',
    links: [
      { path: '/cards', label: '타로 카드 78장 의미 도감' },
      ...neighbors.map((c) => ({ path: `/cards/${cardSlug(c)}`, label: cardLinkLabel(c) })),
    ],
    body: meaning
      ? [
          meaning.story,
          `정방향 — ${meaning.upright}`,
          `역방향 — ${meaning.reversed}`,
          meaning.advice,
        ]
      : [
          `${card.nameKo}(${card.nameEn})는 ${card.arcana === 'major' ? '메이저' : '마이너'} 아르카나 카드입니다.`,
          `정방향 키워드 — ${keywords}`,
          `역방향 키워드 — ${card.keywordsRev.join(' · ')}`,
        ],
  };
}

/**
 * 카드 도감 밖의 고정 페이지.
 * 개인 결과(/r/:shareId)·방·회원 화면은 색인 대상이 아니라 여기에 없다 (robots.txt와 동일 기준).
 */
export const STATIC_PAGE_SEO: readonly PageSeo[] = [
  {
    path: '/',
    title: BRAND.title,
    description: BRAND.metaDescription,
    priority: '1.0',
    navLabel: '홈',
    body: [
      '타로위드는 78장을 직접 섞고 뽑으면 AI 상담사가 그 자리에서 해석해 주는 무료 타로입니다.',
      '오늘의 운세·연애·우정·사업·학업·결혼·행운·금전·건강 아홉 가지 주제 중에서 고를 수 있습니다.',
      '나 혼자 타로는 혼자 카드를 뽑아 바로 해석을 받습니다.',
      '커플 타로는 두 사람이 각자 자기 기기에서 같은 방에 들어와, 서로 뽑은 카드를 실시간으로 보며 관계 관점의 해석을 함께 받습니다.',
      '우정 타로는 2~4명이 같은 방에서 함께 카드를 열어 봅니다.',
      '회원가입 없이 무료로 이용할 수 있고, 같은 카드를 뽑아도 해석은 매번 새로 만들어집니다.',
    ],
  },
  {
    path: '/cards',
    title: withBrand('타로 카드 78장 의미 도감'),
    description:
      '메이저 아르카나 22장과 마이너 아르카나 56장, 타로 카드 78장의 정방향·역방향 의미를 한곳에서 찾아보세요.',
    priority: '0.9',
    navLabel: '카드 도감',
    // 78장 전체로 나가는 링크 — 카드 페이지가 발견되는 유일한 길이다
    links: CARDS.map((c) => ({ path: `/cards/${cardSlug(c)}`, label: cardLinkLabel(c) })),
    body: [
      '타로 카드 78장은 메이저 아르카나 22장과 마이너 아르카나 56장으로 이루어집니다.',
      '마이너 아르카나는 완드·컵·소드·펜타클 네 슈트로 나뉘고, 슈트마다 에이스부터 10까지의 숫자 카드와 페이지·나이트·퀸·킹의 코트 카드가 있습니다.',
    ],
  },
  {
    path: '/glossary',
    title: withBrand('타로 용어 사전'),
    description:
      '아르카나, 슈트, 정방향과 역방향, 스프레드, 포지션 — 타로를 처음 볼 때 헷갈리는 용어를 쉬운 말로 풀었습니다.',
    priority: '0.8',
    navLabel: '타로 용어 사전',
    // 본문은 GLOSSARY 단일 출처에서 그대로 가져온다 — 화면과 정적 HTML이 어긋날 수 없다
    body: GLOSSARY.flatMap((t) => [`${t.term} — ${t.short}`, ...t.body]),
  },
  {
    path: '/solo',
    title: withBrand('나 혼자 타로 — 무료 타로점'),
    description:
      '78장을 직접 섞고 뽑아 AI 상담사의 해석을 받아보세요. 오늘의 운세·연애·금전·건강 등 주제별로 볼 수 있고, 회원가입 없이 무료입니다.',
    priority: '0.8',
    navLabel: '나 혼자 타로',
    body: [
      '나 혼자 타로는 혼자서 카드를 뽑고 해석을 받는 모드입니다. 로그인 없이 바로 이용할 수 있습니다.',
      '주제를 고르고, 78장이 펼쳐진 덱에서 카드를 아래로 끌어내려 뽑습니다. 오늘의 운세는 한 장, 그 밖의 주제는 과거·현재·미래 세 장을 뽑습니다.',
      '뽑은 카드는 정방향과 역방향이 그대로 반영되고, 카드가 놓인 자리(포지션)까지 함께 읽습니다.',
      '고민을 한 줄 적으면 그 상황에 맞춰 해석하고, MBTI를 알려주면 말투와 조언 방식을 맞춥니다. 둘 다 선택 입력입니다.',
    ],
  },
  {
    path: '/info/about',
    title: withBrand('이용 안내'),
    description: '타로 보는 방법, 커플·우정 방 이용법, 하루 이용 횟수와 로그인 혜택을 안내합니다.',
    priority: '0.5',
    navLabel: '이용 안내',
    body: [
      '타로위드를 처음 쓰는 분을 위한 안내입니다.',
      '주제를 고르고 카드를 뽑으면 AI 상담사가 해석해 드립니다. 회원가입은 필요 없습니다.',
      '커플·우정 타로는 방을 만들어 초대 링크나 코드를 보내면, 받은 사람이 가입 없이 들어와 함께 카드를 엽니다.',
      `하루 ${FREE_LIMIT}번은 무료로 볼 수 있고, 그다음부터는 광고 한 편을 보면 한 번씩 이어서 볼 수 있습니다. 횟수는 한국시간 자정에 초기화됩니다.`,
      '로그인하면 지난 타로가 저장되고 닉네임이 방에 자동으로 채워집니다.',
    ],
  },
  {
    path: '/info/faq',
    title: withBrand('자주 묻는 질문'),
    description:
      '무료인지, 해석은 어떻게 만들어지는지, 결과 저장과 방 입장에 대한 답을 모았습니다.',
    priority: '0.5',
    navLabel: '자주 묻는 질문',
    body: [
      '타로위드는 무료입니다. 하루 이용 횟수를 넘기면 광고를 보고 이어서 볼 수 있습니다.',
      '해석은 매번 새로 만들어집니다. 같은 카드를 뽑아도 고민과 카드 조합, 포지션에 따라 다른 이야기가 나옵니다.',
      '결과 저장은 로그인한 경우에만 됩니다. 비회원도 모든 기능을 쓸 수 있지만 기록은 남지 않습니다.',
      '방 입장은 초대 링크나 코드로 합니다. 받는 사람도 가입할 필요가 없습니다.',
      '타로는 재미와 위로를 위한 콘텐츠입니다. 중요한 결정은 스스로 판단해 주세요.',
    ],
  },
  {
    path: '/info/terms',
    title: withBrand('서비스 이용약관'),
    description:
      '무료 이용, 하루 이용 횟수와 광고, 방과 공유 링크, 계정과 탈퇴에 대한 약속을 쉬운 말로 적었습니다.',
    priority: '0.5',
    navLabel: '서비스 이용약관',
    body: [
      '타로위드 서비스 이용약관입니다.',
      '서비스는 무료로 제공되며, 하루 이용 횟수와 광고 정책이 적용됩니다.',
      '방과 공유 링크는 일정 시간이 지나면 만료됩니다.',
      '해석은 재미와 위로를 위한 콘텐츠이며 의료·법률·금전 자문이 아닙니다.',
      '계정은 언제든 탈퇴할 수 있고, 탈퇴하면 저장된 기록도 함께 지워집니다.',
    ],
  },
  {
    path: '/info/privacy',
    title: withBrand('개인정보 처리방침'),
    description:
      '이름·이메일·전화번호를 받지 않습니다. 실제로 저장하는 것과 저장하지 않는 것을 그대로 적었습니다.',
    priority: '0.5',
    navLabel: '개인정보 처리방침',
    body: [
      '타로위드는 이름·이메일·전화번호 같은 개인정보를 받지 않습니다.',
      '소셜 로그인을 하면 제공사가 주는 고유 식별키만 저장합니다. 이름이나 이메일은 저장하지 않습니다.',
      '저장하는 것은 서비스가 만든 표시용 닉네임, 화면 테마, 선택 입력한 MBTI, 그리고 로그인한 경우의 타로 기록입니다.',
      '접속 IP는 이용자 식별에 쓰지 않습니다.',
      '탈퇴하면 저장된 내용이 즉시 파기됩니다.',
    ],
  },
] as const;

/**
 * 모든 정적 HTML에 심는 공통 메뉴.
 *
 * 크롤러가 어느 주소로 들어오든 사이트 전체로 이어지는 길을 갖게 한다 — 사이트맵에만 있고
 * 링크가 없는 주소는 구글이 크롤링을 미룬다. 화면 푸터 메뉴와 같은 구성이어야 하고,
 * 정적 HTML에만 있는 링크를 넣으면 클로킹으로 읽힌다.
 */
export const SITE_NAV: readonly PageLink[] = STATIC_PAGE_SEO.filter((p) => p.navLabel).map((p) => ({
  path: p.path,
  label: p.navLabel as string,
}));

/** 색인 대상 전체 (고정 페이지 + 카드 78장). sitemap·정적 HTML 생성이 모두 이 목록을 쓴다 */
export function allPageSeo(): PageSeo[] {
  return [...STATIC_PAGE_SEO, ...CARDS.map(cardSeo)];
}

/** 경로로 메타데이터 찾기 — SPA 라우트에서 document.title을 맞출 때 사용 */
export function pageSeoOf(path: string): PageSeo | undefined {
  return allPageSeo().find((p) => p.path === path);
}
